import React, { useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Box,
  Stack,
  Divider,
  Radio,
  FormControl,
  InputLabel,
} from '@mui/material';
import { SignOnBehalfIcon } from 'src/pages/schedule/therapist/UI-Improvment/schedule-icon';
import { useSelector } from 'react-redux';
import SignatureOnBehalfSkeleton from './signature-on-behalf-skeleton';

/**
 * BRK-2073: mandatory reason + consent dialog for document actions taken ON BEHALF OF another
 * therapist under `COMPLIANCE_DOCUMENT_ON_BEHALF` — unsign and edit (create reuses it in the
 * sibling stories). Extracted from the sign-on-behalf modal's scaffold: same identity cards
 * (the on-behalf-of provider from `getTherapistInfoReducer` — the opener dispatches
 * `getTherapistInfoAction({providerId})` — and the acting user from the profile), same REASONS
 * list, two consents. The signature machinery is deliberately absent — no PIN, no typed
 * signature, no signing wizard: PIN is a signature concept, and these actions are not signatures.
 * The reason lands in the audit trail (`proxy_reason` on the unsign rows;
 * `webhook_entity.on_behalf_reason` → the RESUBMIT row for edits).
 */

interface OnBehalfReasonModalProps {
  onClose: () => void;
  //  consentConfirmation travels with the action rather than being assumed by the caller,
  //  mirroring the sign modal's contract.
  onConfirm: (document: any, reason: string, consentConfirmation: boolean) => void;
  document: any;
  /** Dialog title, e.g. "Unsigning on behalf of" / "Editing on behalf of". */
  title: string;
  /** The consent sentence's verb phrase, e.g. "unsigning this document". */
  actionPhrase: string;
  onBehalfOfLabel?: string;
  actorLabel?: string;
  confirmLabel?: string;
}

//  Exported for the sibling on-behalf dialogs (BRK-2074 create) — ONE canonical reason list for
//  every document-on-behalf verb, so the audit trail stays queryable by a closed vocabulary.
export const REASONS = [
  'PTO / Vacation Coverage',
  'System Access Issues',
  'Illness / Out Sick',
  'Shift Coverage / PRN Support',
  'Clinician No Longer at Facility',
  'Documentation Assistance – Per Handoff',
  'EHR Login Delay / Pending Credentials',
  'Weekend or Holiday Coverage',
  'Emergency Absence / Unplanned Leave',
  'Technical Error / System Crash',
];

const IdentityCard: React.FC<{
  label: string;
  name: string;
  credentials?: string;
  licenseNo?: string;
}> = ({ label, name, credentials, licenseNo }) => (
  <Box sx={{ display: 'flex', gap: 2 }}>
    <Box
      sx={{
        background: '#F5F7FF',
        borderRadius: '12px',
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 1,
      }}
    >
      <SignOnBehalfIcon />
    </Box>
    <Box>
      <Typography
        variant="subtitle2"
        color="text.secondary"
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Typography variant="boldText">Name</Typography>
        <Typography
          variant="body3"
          noWrap
        >
          {name}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant="boldText">Credentials</Typography>
        <Typography
          variant="body3"
          noWrap
        >
          {credentials}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant="boldText">License No.</Typography>
        <Typography
          variant="body3"
          noWrap
        >
          {licenseNo}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const OnBehalfReasonModal: React.FC<OnBehalfReasonModalProps> = ({
  onClose,
  onConfirm,
  document,
  title,
  actionPhrase,
  onBehalfOfLabel = 'Therapist',
  actorLabel = 'Acting user',
  confirmLabel = 'Continue',
}) => {
  const actorProfile = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data
  );
  const onBehalfOfInfo = useSelector((state: any) => state.getTherapistInfoReducer);

  const [reason, setReason] = useState('');
  const [agree, setAgree] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const onBehalfOfName = [
    onBehalfOfInfo?.response?.data?.lastName,
    onBehalfOfInfo?.response?.data?.firstName,
  ]
    .filter(Boolean)
    .join(' ');

  //  The identity must have actually resolved: the consent sentence names the on-behalf-of
  //  provider, so a blank card (missing target id, failed lookup) must not be confirmable.
  const isConfirmEnabled = Boolean(reason && agree && confirm && onBehalfOfName);

  const handleConfirm = () => {
    if (!isConfirmEnabled) return;
    onConfirm(document, reason, agree);
  };

  if (onBehalfOfInfo?.loading) {
    return <SignatureOnBehalfSkeleton />;
  }

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Stack
            direction="row"
            spacing={4}
          >
            <IdentityCard
              label={onBehalfOfLabel}
              name={onBehalfOfName}
              credentials={onBehalfOfInfo?.response?.data?.credentials}
              licenseNo={onBehalfOfInfo?.response?.data?.licenseNo}
            />
            <Divider
              orientation="vertical"
              flexItem
            />
            <IdentityCard
              label={actorLabel}
              name={[actorProfile?.lastName, actorProfile?.firstName].filter(Boolean).join(' ')}
              credentials={actorProfile?.credentials}
              licenseNo={actorProfile?.licenseNumber}
            />
          </Stack>
        </Box>
        <Stack
          flexDirection={'column'}
          rowGap={1.5}
        >
          <FormControl
            fullWidth
            sx={{ mb: 2 }}
          >
            <InputLabel id="on-behalf-reason-label">Reason</InputLabel>
            <Select
              labelId="on-behalf-reason-label"
              value={reason}
              label="Reason"
              onChange={(e) => setReason(e.target.value)}
              renderValue={(selected) =>
                selected ? selected : <span style={{ color: '#6C737F' }}>Reason</span>
              }
            >
              {REASONS.map((r) => (
                <MenuItem
                  key={r}
                  value={r}
                >
                  <Radio
                    checked={reason === r}
                    value={r}
                    sx={{ padding: 0, marginRight: 1.5 }}
                  />
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I understand this action is recorded in the document&apos;s audit trail with my
                identity and the reason selected above.
              </Typography>
            }
            sx={{ alignItems: 'flex-start' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={confirm}
                onChange={(e) => setConfirm(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I confirm that I am {actionPhrase} on behalf of <b>{onBehalfOfName}</b> and take
                full responsibility for this action.
              </Typography>
            }
            sx={{ alignItems: 'flex-start', mt: 1 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!isConfirmEnabled}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </>
  );
};

export default OnBehalfReasonModal;
